import moment from "moment";
import getUrl from "../../utility/getUrl";
import { RawItemType } from "./types";

const getToken = (auth: any) => {
  const token = `Bearer ${auth.accessToken}`;
  // const token = `Bearer ${AUTH_TOKEN}`;
  return token;
};

export function queryAllTracesCount(fromDate: Date, toDate: Date) {
  const dtFrom = formatDateYMD(fromDate);
  const dtTill = formatDateYMD(toDate);
  return `traces
    | where timestamp >= todatetime("${dtFrom}") and timestamp <= todatetime("${dtTill}")
    | count`;
}

const FETCH_TIMEOUT_DURATION = 60000;

function queryAllTraces(fromDate: Date, toDate: Date) {
  const dtFrom = formatDateYMD(fromDate);
  const dtTill = formatDateYMD(toDate);
  return `traces
      | where timestamp >= todatetime("${dtFrom}") and timestamp <= todatetime("${dtTill}")
      | where message startswith "PublishProject. message:"
      | extend publishId = tostring(customDimensions.publishId)
      | extend name = tostring(customDimensions.Name)
      | extend folder = tostring(customDimensions.Folder)
      | extend projectName = tostring(customDimensions.projectName)
      | extend projectFolder = tostring(customDimensions.projectFolder)
      | extend organizationId = tostring(customDimensions.organizationId)
      | extend fileCount = toint(customDimensions.fileCount)
      | extend filesCount = toint(customDimensions.filesCount)
      | extend language = tostring(customDimensions.language)
      | extend publishType = tostring(customDimensions.publishType)
      | project timestamp, publishId, message, name, folder, organizationId, fileCount, projectName, projectFolder, filesCount, language, publishType, operation_Id
      | order by timestamp desc`;
}

function queryAllExceptions(fromDate: Date, toDate: Date) {
  const dtFrom = formatDateYMD(fromDate);
  const dtTill = formatDateYMD(toDate);
  return `exceptions
    | where timestamp >= todatetime("${dtFrom}") and timestamp <= todatetime("${dtTill}")
    | extend formattedMessage = tostring( customDimensions.FormattedMessage)
    | extend publishId = tostring(customDimensions.publishId)
    | where isempty(publishId) == false
    | extend message = strcat(formattedMessage, " / ", outerMessage, " / ", outerMethod, " / ", innermostMessage)
    | project timestamp, publishId, message, operation_Id
    | order by timestamp asc`;
}

function formatDateYMD(dt: Date) {

  if (moment.isMoment(dt))
  {
    dt = dt.toDate();
  }
  
  const fill = (nr: number) => {
    let s = `${nr}`;
    if (s.length < 2) {
      return `0${s}`;
    }
    return s;
  };
  return `${dt.getFullYear()}-${fill(dt.getUTCMonth() + 1)}-${fill(
    dt.getUTCDate()
  )}`;
}

async function fetchQuery(auth: any, query: string) {
  const url = getUrl("ai");
  const payload = {
    query: query,
  };

  const options = {
    method: "POST",
    headers: new Headers({
      "Content-Type": "application/json",
      Authorization: getToken(auth),
    }),
    body: JSON.stringify(payload),
  };

  const response = await fetchWithTimeout(url, options, FETCH_TIMEOUT_DURATION);
  if (response.ok) {
    const result = (await response.json()) as any;

    return result;
  } else {
    throw new Error(response.statusText);
  }
}

async function fetchTracesCount(
  auth: any,
  fromDate: Date,
  toDate: Date
): Promise<number> {
  const resultTraces = await fetchQuery(
    auth,
    queryAllTracesCount(fromDate, toDate)
  );
  return resultTraces.tables[0].rows[0][0];
}

async function fetchAll(
  auth: any,
  fromDate: Date,
  toDate: Date
): Promise<RawItemType[]> {
  const resultTraces = await fetchQuery(auth, queryAllTraces(fromDate, toDate));
  const traceItems: RawItemType[] = resultTraces.tables[0].rows.map(
    (row: any[]) => {
      const [
        timeStamp,
        publishId,
        message,
        name,
        folder,
        organizationId,
        fileCount,
        projectName,
        projectFolder,
        filesCount,
        language,
        publishType,
        operationId,
      ] = row;

      const item = {
        timeStamp: new Date(timeStamp),
        publishId,
        message,
        name: name ? name : projectName,
        folder: folder ? folder : projectFolder,
        organizationId,
        fileCount: filesCount ?? fileCount,
        language,
        publishType,
        operationId,
      };
      return item;
    }
  );

  const resultExceptions = await fetchQuery(
    auth,
    queryAllExceptions(fromDate, toDate)
  );
  const exceptionItems: RawItemType[] = resultExceptions.tables[0].rows.map(
    (row: any[]) => {
      const [timeStamp, publishId, message, operationId] = row;
      const item = {
        timeStamp: new Date(timeStamp),
        publishId,
        message,
        operationId,
      };
      return item;
    }
  );
  return traceItems.concat(exceptionItems);
}

async function fetchOrganizationNameMap(auth: any, organizationIds: string[]) {
  const url = getUrl("organizationMap");

  const payload = { ids: organizationIds };
  const options = {
    method: "POST",
    headers: new Headers({
      Authorization: getToken(auth),
      "Content-Type": "application/json",
    }),
    body: JSON.stringify(payload),
  };

  const response = await fetchWithTimeout(url, options, FETCH_TIMEOUT_DURATION);
  if (response.ok) {
    const orgNameMap: Record<string, string> = await response.json();
    return orgNameMap;
  } else {
    throw new Error(response.statusText);
  }
}

const fetchWithTimeout = (
  url: RequestInfo,
  options: RequestInit,
  ms: number
) => {
  const controller = new AbortController();
  const promise = fetch(url, { ...options, signal: controller.signal });
  const timeout = setTimeout(() => controller.abort(), ms);
  return promise.finally(() => clearTimeout(timeout));
};

export { fetchAll, fetchTracesCount, fetchOrganizationNameMap };
